import { collection, doc, getDocs, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';

const AUTH_CONTINUE_URL = process.env.REACT_APP_AUTH_CONTINUE_URL || 'https://mpsvns.web.app';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

export async function listAllUsers() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listPendingInvites() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'invites'));
  return snap.docs.map((d) => ({ id: d.id, email: d.id, ...d.data() }));
}

export async function deleteInvite(email) {
  if (!db) throw new Error('Firestore not configured.');
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) throw new Error('Email is required.');
  await deleteDoc(doc(db, 'invites', normalizedEmail));
}

/** Spark plan: write invite; user completes registration on the signup tab. */
async function createSchoolUserViaInvite({ email, name, role }) {
  const normalizedEmail = normalizeEmail(email);
  await setDoc(doc(db, 'invites', normalizedEmail), {
    email: normalizedEmail,
    name: String(name).trim(),
    role,
    createdAt: new Date().toISOString(),
  });
  return { email: normalizedEmail, name, role, method: 'invite' };
}

/** Blaze plan: Cloud Function creates Auth user immediately. */
async function createSchoolUserViaFunction({ email, name, role }) {
  if (!functions) throw new Error('Functions not available');
  const callable = httpsCallable(functions, 'createSchoolUser');
  const result = await callable({ email, name, role });
  return { ...result.data, method: 'function' };
}

export async function createSchoolUser({ email, name, role }) {
  try {
    return await createSchoolUserViaFunction({ email, name, role });
  } catch {
    return createSchoolUserViaInvite({ email, name, role });
  }
}

export async function registerInvitedUser({ email, password }) {
  if (!auth || !db) throw new Error('Firebase not configured.');
  const normalizedEmail = normalizeEmail(email);
  const inviteRef = doc(db, 'invites', normalizedEmail);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) {
    throw new Error('No invitation found for this email. Contact the school admin.');
  }
  const invite = inviteSnap.data();
  const cred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
  await setDoc(doc(db, 'users', cred.user.uid), {
    email: normalizedEmail,
    name: invite.name,
    role: invite.role,
    createdAt: new Date().toISOString(),
  });
  await deleteDoc(inviteRef);
  return cred.user;
}

export async function sendPasswordResetToUser(email) {
  if (!auth) throw new Error('Auth not configured.');
  await sendPasswordResetEmail(auth, normalizeEmail(email), {
    url: AUTH_CONTINUE_URL,
    handleCodeInApp: false,
  });
}

/** Server-side student profile + students/{admno} doc (admin SDK, bypasses client rule limits). */
export async function ensureStudentProfile(student) {
  if (!functions) throw new Error('Cloud Functions not available.');
  const callable = httpsCallable(functions, 'ensureStudentProfile');
  const result = await callable({
    admno: student.admno || student.studentId,
    name: student.studentName || student.name,
    class: student.class,
    studentCsvId: student.studentCsvId || null,
  });
  return result.data;
}

/** Create Firebase Auth accounts for students (batches of up to 25). Requires deployed Cloud Function. */
export async function importStudentAccountsBatch(students) {
  if (!functions) throw new Error('Cloud Functions not available.');
  const callable = httpsCallable(functions, 'importStudentAccounts');
  const result = await callable({ students });
  return result.data;
}

/** Trigger absent-student email report (admin only, requires Cloud Function). */
export async function sendAbsentReportEmail({ date }) {
  if (!functions) throw new Error('Cloud Functions not available.');
  const callable = httpsCallable(functions, 'sendAbsentReportEmail');
  const result = await callable({ date: date || new Date().toISOString().slice(0, 10) });
  return result.data;
}

/** Write teacherSeeds docs so teachers get Firestore profile on first login. */
export async function prepareTeacherSeeds(teachers) {
  if (!db) throw new Error('Firestore not configured.');
  const results = [];
  for (const t of teachers) {
    const email = normalizeEmail(t.email);
    await setDoc(doc(db, 'teacherSeeds', email), {
      email,
      name: String(t.name || '').trim(),
      role: 'TEACHER',
      allClasses: t.allClasses !== false,
      assignedClasses: t.assignedClasses || [],
      updatedAt: new Date().toISOString(),
    });
    results.push(email);
  }
  return results;
}
